<template lang="pug">
    .three-js-scene(:style="{width: width, height: height}")
      .scene(ref="threeJsScene", :style="{opacity: modelLoaded ? 1 : 0, width: this.width}")
      car-actions(@action="onActionClick",
        :doorsOpened="doorsOpened",
        :trunkOpened="trunkOpened",
        :lightsEnabled="!lightsEnabled",
        :ambientLightEnabled="ambientLightEnabled")
      .preloader(v-if="!modelLoaded" :key="preloaderKey")
          video(:src="videoUrl", muted, autoplay, loop, playsinline)
      .preloader-line(v-show="!modelLoaded", :style="{width: this.loadedPerscents * 99 + '%'}")
        .line
        .value {{Math.round(this.loadedPerscents * 99)}}%
</template>
<script>
import ThreeWrapper from "./ThreeJsScene/ThreeWrapper";
import CameraController from "./ThreeJsScene/CameraController";
import CarSceneController from "./ThreeJsScene/CarSceneController";
import {mapActions, mapGetters} from "vuex";
import CarActions from "./CarActions";
import isMobileMixin from "../mixins/isMobileMixin";
import CameraPoloController from "./ThreeJsScene/CameraPoloController";
import CarScenePoloController from "./ThreeJsScene/CarScenePoloController";

export default {
  name: 'ThreeJsScene',
  mixins: [isMobileMixin],
  components: {CarActions},
  props: ['width', 'height'],
  data() {
    return {
      doorsOpened: false,
      trunkOpened: false,
      lightsEnabled: true,
      ambientLightEnabled: false,
      preloaderKey: 'preloaderKey',
      modelLoaded: false,
      loadedPerscents: 0,
    }
  },

  created() {
    this.threeWrapper = null
    this.cameraController = null
    this.carSceneController = null

    this.sounds = {
      'doors-opened': new Audio('/sounds/door-open_trim.mp3'),
      'doors-closed': new Audio('/sounds/door-close_trim.mp3'),
      'trunk-opened': new Audio('/sounds/trunk-open.mp3'),
      'trunk-closed': new Audio('/sounds/trunk-close.mp3')
    }
  },

  mounted() {
    this.initThreeJs();
    window.addEventListener('resize', this.onWindowResize)
  },

  destroyed() {
    this.threeWrapper.dispose()
    window.removeEventListener('resize', this.onWindowResize)
  },

  computed: {
    ...mapGetters(['carColor', 'carWheels', 'carInteriorColor', 'uspData', 'uspWindowVisible', 'zoom', 'insideCabine', 'carModel']),

    videoUrl() {
      return this.carModel === 'jetta' ? '/video/Intro_Jetta_6_2200.mp4' : '/video/intro_polo_2500.mp4'
    },

    resultUspData () {
      return this.uspWindowVisible ? this.uspData : null
    },

    carModel () {
      return this.$route.name === 'polo' ? 'polo' : 'jetta'
    },

    currentUspData() {
      const data = this.uspWindowVisible ? this.uspData : null
      return data
    }
  },

  watch: {
    currentUspData(val) {
      this.cameraController.setSelectedUsp(val)
    },
    carInteriorColor (color) {
      if (this.carSceneController) {
        this.carSceneController.setInteriorColor(color)
      }
    },
    carColor(color) {
      if (this.carSceneController) {
        this.carSceneController.setCarColor(color)
      }
    },

    carWheels(wheelsData) {
      if (this.carSceneController) {
        this.carSceneController.setCarWheels(wheelsData)
      }
    },

    insideCabine(inside) {
      this.ambientLightEnabled = false
      if (inside) {
        if (!this.cameraController._currentPoint || !this.cameraController._currentPoint.insideCabine) {
          this.cameraController.setCameraPoint(null)
          this.cameraController.setInsideCabine()
        } else {
          this.cameraController.setInsideCabine()
        }
      } else {
        if (this.carSceneController._ambientLightEnabled) this.carSceneController.toggleAmbientLight()
        if (!this.cameraController._currentPoint || this.cameraController._currentPoint.insideCabine) {
          this.cameraController.setCameraPointByName('full-car')
          this.cameraController.setOutsideCabine()
        } else {
          this.cameraController.setOutsideCabine()
        }
      }
    },

    zoom (zoom) {
      if (this.cameraController && zoom) {
        this.cameraController.zoom(zoom)
      }
    },

    uspData() {
      this.carSceneController.stopRenderAlways(2)
    },

    resultUspData (data) {
      if (this.cameraController) {
        if (data) {
          const point = this.cameraController.setCameraPointByName(data.pointName)
          this.setInsideCabine(point != null && point.insideCabine === true)
        }
      }
    },
  },

  methods: {
    ...mapActions(['setUspByCameraName', 'setInsideCabine']),
    initThreeJs() {

      this.threeWrapper = new ThreeWrapper(this.$refs.threeJsScene, {
        backgroundColor: 0x000000,
        fov: this.isMobile ? 60 : 50
      })

      const carControllerParams = {
        isMobile: this.isMobile,
        isChinaMobile: this.isChinaMobile,
        threeWrapper: this.threeWrapper,
        cameraController: this.cameraController,
        onModelProgress: (loadedPerscents) => {
          this.loadedPerscents = loadedPerscents
        },
        onModelLoad: () => {
          this.$nextTick(() => {
            this.modelLoaded = true
            if (this.carModel === 'polo') {
              this.carSceneController.setCarWheels(this.carWheels)
            }
            this.carSceneController.setCarColor(this.carColor)
            this.carSceneController.setInteriorColor(this.carInteriorColor)
            this.cameraController.setCameraPointByName('full-car')
          })

          this.$emit('modelloaded')
        },
        onDoorsOpened: () => {
          this.sounds['doors-opened'].play()
        },
        onDoorsClosed: () => {
          this.sounds['doors-closed'].play()
        },
        onTrunkOpened: () => {
          this.sounds['trunk-opened'].play()
        },
        onTrunkClosed: () => {
          this.sounds['trunk-closed'].play()
        }
      }

      this.threeWrapper.loadHDR('/3d/hdr/autoshop_NN.hdr').then(() => {
        if (this.carModel === 'polo') {
          this.carSceneController = new CarScenePoloController(carControllerParams)
        } else {
          this.carSceneController = new CarSceneController(carControllerParams)
        }
      })

      if (this.carModel === 'polo') {
        this.cameraController = new CameraPoloController({
          threeWrapper: this.threeWrapper,
          onPointsUpdated: (points) => {
            this.$emit('points', points)
          },
          onPointClick: (point) => {
            if (point.markerPosition) {
              if (this.cameraController.setCameraPoint(point)) {
                this.setUspByCameraName(point.name)
                this.setInsideCabine(point.insideCabine ? true : false)
                this.carSceneController.stopRenderAlways(2)
              }
            } else {
              switch (point.name) {
                case 'trunk-action':
                  this.carSceneController.toggleTrunk()
                  break

                case 'lights-action': {
                  this.carSceneController.toggleLights()
                  // let enabled = this.carSceneController.toggleLights()
                  // this.threeWrapper.setBoomEnabled(enabled)
                }
                  break

                case 'door-action':
                  this.carSceneController.toggleDoors()
                  // let enabled = this.carSceneController.toggleLights()
                  // this.threeWrapper.setBoomEnabled(enabled)
                  break
              }
            }
          }
        })
      } else {
        this.cameraController = new CameraController({
          threeWrapper: this.threeWrapper,
          onPointsUpdated: (points) => {
            this.$emit('points', points)
          },
          onPointClick: (point) => {
            if (point.markerPosition) {
              if (this.cameraController.setCameraPoint(point)) {
                this.setUspByCameraName(point.name)
                this.setInsideCabine(point.insideCabine ? true : false)
                this.carSceneController.stopRenderAlways(2)
              }
            } else {
              switch (point.name) {
                case 'trunk-action':
                  this.carSceneController.toggleTrunk()
                  break

                case 'lights-action': {
                  this.carSceneController.toggleLights()
                  // let enabled = this.carSceneController.toggleLights()
                  // this.threeWrapper.setBoomEnabled(enabled)
                }
                  break

                case 'door-action':
                  this.carSceneController.toggleDoors()
                  // let enabled = this.carSceneController.toggleLights()
                  // this.threeWrapper.setBoomEnabled(enabled)
                  break
              }
            }
          }
        })
      }
    },

    onActionClick(action) {
      switch (action) {
        case 'trunk':
          this.carSceneController.toggleTrunk()
          this.trunkOpened = this.carSceneController._trunkOpened
          break
        case 'doors':
          this.carSceneController.toggleDoors()
          this.doorsOpened = this.carSceneController._doorsOpened
          break
        case 'lights':
          this.carSceneController.toggleLights()
          this.lightsEnabled = this.carSceneController._lightEnabled
          break
        case 'ambient-light':
          this.carSceneController.toggleAmbientLight()
          this.ambientLightEnabled = this.carSceneController._ambientLightEnabled
          break
      }
    },

    onWindowResize() {
      if (this.threeWrapper) this.threeWrapper.updateProjectionMatrix()
    }
  }
}
</script>